import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  Alert
} from 'reactstrap';
import apiService from 'service/apiService';
import merchantService from 'service/merchantService.ts';
import ConfirmationModal from 'components/ConfirmationModal';

function CreateApplicationModal({ isOpen = false, toggle = () => {}, merchantId = null, onCreated = () => {} }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [merchant, setMerchant] = useState(null);
  const [requestedAmount, setRequestedAmount] = useState('');
  const [totalSubscribers, setTotalSubscribers] = useState('');
  const [totalMonthlyRevenue, setTotalMonthlyRevenue] = useState('');
  const [monitoringCenter, setMonitoringCenter] = useState('');
  const [monitoringCenterId, setMonitoringCenterId] = useState(null);
  const [monitoringCenterOptions, setMonitoringCenterOptions] = useState([]);

  // Success/Error modal state
  const [modalOpen, setModalOpen] = useState(false);
  const defaultModalConfig = {
    title: '',
    message: '',
    icon: 'ni ni-check-bold',
    confirmText: 'OK',
    cancelText: 'Close',
    confirmColor: 'primary',
    cancelColor: 'secondary',
    onConfirm: null,
    onCancel: null,
    showCancelButton: true
  };
  const [modalConfig, setModalConfig] = useState(defaultModalConfig);
  const openModal = (config) => {
    setModalConfig({ ...defaultModalConfig, ...config });
    setModalOpen(true);
  };
  const closeModal = () => setModalOpen(false);

  // Function to clear all input fields
  const clearInputs = () => {
    setRequestedAmount('');
    setTotalSubscribers('');
    setTotalMonthlyRevenue('');
    setMonitoringCenter('');
    setMonitoringCenterId(null);
    setError('');
  };

  // Enhanced toggle function that clears inputs when closing
  const handleToggle = () => {
    if (isOpen) {
      clearInputs();
    }
    toggle();
  };

  // Load merchant details and monitoring centers when opening
  useEffect(() => {
    const load = async () => {
      setError('');
      setMerchant(null);
      if (!isOpen || !merchantId) return;
      setLoading(true);
      try {
        const [merchantData, monitoringCentres] = await Promise.all([
          (async () => {
            const token = await apiService.getToken();
            const { data } = await merchantService.getMerchantById(token, merchantId);
            return data || null;
          })(),
          apiService.getMonitoringCentres().catch(() => [])
        ]);
        
        setMerchant(merchantData);
        // Handle both array response and paginated response
        const monitoringCenters = Array.isArray(monitoringCentres) ? monitoringCentres : (monitoringCentres?.content || []);
        const monitoringOptions = monitoringCenters.map((mc) => {
          // Handle the new structure with id and legal_name
          if (typeof mc === 'object' && mc !== null) {
            const id = mc.id;
            const name = mc.legal_name || mc['legal-name'] || mc.legalName || mc.name;
            return { 
              value: name, 
              label: name,
              name: name,
              id: id
            };
          }
          // Fallback for old string format
          return { 
            value: mc, 
            label: mc,
            name: mc,
            id: null
          };
        });
        setMonitoringCenterOptions(monitoringOptions);
        
        // Set default monitoring center since we only have one
        if (monitoringOptions.length > 0) {
          setMonitoringCenter(monitoringOptions[0].value);
          setMonitoringCenterId(monitoringOptions[0].id);
        }
      } catch (e) {
        setError('Error loading merchant details.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, merchantId]);

  const handleAmountChange = (e) => {
    let v = e.target.value || '';
    v = v.replace(/\D/g, '');
    if (v.length > 10) v = v.slice(0, 10);
    setRequestedAmount(v);
  };

  const handleSubscribersChange = (e) => {
    let v = e.target.value || '';
    v = v.replace(/\D/g, '');
    if (v.length > 10) v = v.slice(0, 10);
    setTotalSubscribers(v);
  };

  const handleRevenueChange = (e) => {
    let v = e.target.value || '';
    // Permitir dígitos y un punto decimal
    v = v.replace(/[^\d.]/g, '');
    // Solo un punto decimal
    const parts = v.split('.');
    if (parts.length > 2) {
      v = parts[0] + '.' + parts.slice(1).join('');
    }
    // Limitar a 2 decimales
    if (parts[1] && parts[1].length > 2) {
      v = parts[0] + '.' + parts[1].slice(0, 2);
    }
    // Limitar a 15 dígitos totales
    if (v.replace('.', '').length > 15) {
      const withoutDot = v.replace('.', '');
      const truncated = withoutDot.slice(0, 15);
      if (v.includes('.')) {
        const dotIndex = v.indexOf('.');
        v = truncated.slice(0, dotIndex) + '.' + truncated.slice(dotIndex);
      } else {
        v = truncated;
      }
    }
    setTotalMonthlyRevenue(v);
  };

  const isValid = useMemo(() => {
    const amountValid = requestedAmount && requestedAmount.trim() !== '' && /^\d{1,10}$/.test(requestedAmount);
    const subscribersValid = totalSubscribers && totalSubscribers.trim() !== '' && /^\d{1,10}$/.test(totalSubscribers);
    const revenueValid = totalMonthlyRevenue && totalMonthlyRevenue.trim() !== '' && /^\d+(\.\d{1,2})?$/.test(totalMonthlyRevenue);
    const monitoringValid = monitoringCenter && monitoringCenter.trim() !== '';
    return amountValid && subscribersValid && revenueValid && monitoringValid;
  }, [requestedAmount, totalSubscribers, totalMonthlyRevenue, monitoringCenter]);

  // Individual field validation for error messages
  const getFieldError = (field, value) => {
    if (!value || value.trim() === '') return null;
    
    switch (field) {
      case 'requestedAmount':
        if (!/^\d{1,10}$/.test(value)) return 'Amount must be up to 10 digits.';
        break;
      case 'totalSubscribers':
        if (!/^\d{1,10}$/.test(value)) return 'Subscribers must be a whole number.';
        break;
      case 'totalMonthlyRevenue':
        if (!/^\d+(\.\d{1,2})?$/.test(value)) return 'Revenue must be a valid decimal number.';
        break;
    }
    return null;
  };

  const buildApplicationPayload = () => {
    // Normalizar fechas a YYYY-MM-DD
    const toIsoDate = (d) => {
      if (!d) return undefined;
      try {
        if (typeof d === 'string' && d.includes('-')) return d;
        if (typeof d === 'string' && d.includes('/')) {
          const p = d.split('/');
          if (p.length === 3) return `${p[2]}-${p[0]}-${p[1]}`;
        }
        const date = new Date(d);
        if (!isNaN(date.getTime())) {
          const yyyy = date.getFullYear();
          const mm = String(date.getMonth() + 1).padStart(2, '0');
          const dd = String(date.getDate()).padStart(2, '0');
          return `${yyyy}-${mm}-${dd}`;
        }
      } catch (_) {}
      return undefined;
    };

    const m = merchant || {};
    const base = m.merchant || m; // algunos endpoints devuelven sin anidar
    const addresses = m.addresses || [];
    const owners = m.owners || [];
    const banks = m.banks || [];

    return {
      amount: parseFloat(requestedAmount),
      totalSubscribers: parseInt(totalSubscribers),
      totalMonthlyRevenue: parseFloat(totalMonthlyRevenue),
      monitoringCenter: monitoringCenter,
      monitoringCenterId: monitoringCenterId,
      merchantId: merchantId || base.id || undefined,
      merchantPayload: {
        id: merchantId || base.id || undefined,
        type: base.type || 'DEALER',
        legalName: base.legalName,
        dbaName: base.dbaName,
        federalTaxId: base.federalTaxId,
        stateOfInc: base.stateOfInc,
        industry: base.industry,
        businessStructure: base.businessStructure,
        website: base.website,
        dateEstablished: toIsoDate(base.dateEstablished),
        phoneNumber: base.phoneNumber || undefined,
        addresses: (addresses || []).map((addr) => ({
          ...(addr && addr.id ? { id: addr.id } : {}),
          addressType: addr.addressType || 'PHYSICAL',
          street1: addr.street1 || addr.streetAddress || '',
          street2: addr.street2 || addr.streetAddress2 || addr.unit || null,
          city: addr.city,
          state: addr.state,
          zipCode: addr.zipCode,
          country: addr.country || 'USA',
          phoneNumber: addr.phoneNumber || null
        })),
        owners: (owners || []).map((o) => {
          const ow = o?.owner || o;
          return {
            ...(ow && ow.id ? { id: ow.id } : {}),
            firstName: ow.firstName,
            middleName: ow.middleName,
            lastName: ow.lastName,
            gender: ow.gender || 'OTHER',
            birthDate: ow.birthDate,
            citizenship: ow.citizenship,
            ownershipPercentage: parseFloat(ow.ownershipPercentage ?? 0),
            socialSecurityNumber: ow.socialSecurityNumber,
            contacts: (o.contacts || ow.contacts || []).map((c) => ({
              ...(c && c.id ? { id: c.id } : {}),
              type: String(c.type || '').toUpperCase() === 'PHONE' ? 'PHONE' : 'EMAIL',
              value: c.value,
              isPrimary: typeof c.isPrimary === 'boolean' ? c.isPrimary : (String(c.primary || '').toLowerCase() === 'yes' || String(c.primary || '').toLowerCase() === 'true')
            })),
            addresses: (o.addresses || ow.addresses || []).map((a) => ({
              ...(a && a.id ? { id: a.id } : {}),
              addressType: a.addressType || 'HOME',
              street1: a.street1 || a.streetAddress || '',
              street2: a.street2 || a.streetAddress2 || null,
              city: a.city,
              state: a.state,
              zipCode: a.zipCode,
              country: a.country || 'USA',
              phoneNumber: a.phoneNumber || null
            }))
          };
        }),
        banks: (banks || []).map((b) => ({
          ...(b && b.id ? { id: b.id } : {}),
          bankName: b.bankName,
          accountName: b.accountName,
          number: b.number,
          bankAddress: b.bankAddress ?? '',
          routingNumber: b.routingNumber,
          bankCity: b.bankCity ?? '',
          bankState: b.bankState ?? '',
          bankCountry: b.bankCountry ?? 'USA',
          type: (b.type || '').toString().toUpperCase() || 'CHECKING'
        }))
      }
    };
  };

  const handleCreate = async () => {
    if (!isValid) return;
    setSubmitting(true);
    setError('');
    try {
      const payload = buildApplicationPayload();
      await apiService.createApplication(payload);
      // Success modal
      openModal({
        title: 'Success',
        message: 'Application created successfully!',
        icon: 'ni ni-check-bold',
        confirmColor: 'success',
        confirmText: 'OK',
        showCancelButton: false,
        onConfirm: () => {
          setRequestedAmount('');
          setTotalSubscribers('');
          setTotalMonthlyRevenue('');
          setMonitoringCenter('');
          onCreated(payload);
          toggle();
          navigate('/applications');
        }
      });
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Error creating application.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderReadOnlyInput = (id, label, value) => (
    <FormGroup className="mb-2">
      <Label htmlFor={id} className="mb-1">{label}</Label>
      <Input id={id} type="text" value={value || ''} readOnly disabled className="bg-light border-secondary" bsSize="sm" />
    </FormGroup>
  );

  return (
    <>
      <Modal isOpen={isOpen} toggle={handleToggle} size="lg">
        <ModalHeader toggle={handleToggle}>Create Application</ModalHeader>
        <ModalBody className="pt-2 pb-2">
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <>
              {error && (
                <Alert color="danger">{error}</Alert>
              )}
              {merchant ? (
                <Form onSubmit={(e) => e.preventDefault()}>
                  <Row>
                    <Col md="6">
                      {renderReadOnlyInput('legalName', 'Legal Name', (merchant.merchant?.legalName || merchant.legalName))}
                    </Col>
                    <Col md="6">
                      {renderReadOnlyInput('dbaName', 'DBA Name', (merchant.merchant?.dbaName || merchant.dbaName))}
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      {renderReadOnlyInput('federalTaxId', 'Federal Tax ID', (merchant.merchant?.federalTaxId || merchant.federalTaxId))}
                    </Col>
                    <Col md="6">
                      {renderReadOnlyInput('stateOfInc', 'State of Formation', (merchant.merchant?.stateOfInc || merchant.stateOfInc))}
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      {renderReadOnlyInput('industry', 'Industry', (merchant.merchant?.industry || merchant.industry))}
                    </Col>
                    <Col md="6">
                      {renderReadOnlyInput('businessStructure', 'Business Structure', (merchant.merchant?.businessStructure || merchant.businessStructure))}
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      {renderReadOnlyInput('website', 'Website', (merchant.merchant?.website || merchant.website))}
                    </Col>
                    <Col md="6">
                      {renderReadOnlyInput('dateEstablished', 'Date Established', (merchant.merchant?.dateEstablished || merchant.dateEstablished))}
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      {renderReadOnlyInput('phoneNumber', 'Phone Number', (merchant.merchant?.phoneNumber || merchant.phoneNumber))}
                    </Col>
                  </Row>

                  {/* Addresses summary */}
                  <Row>
                    <Col md="12">
                      <h5 className="text-primary mt-2 mb-2">Addresses</h5>
                      {Array.isArray(merchant.addresses) && merchant.addresses.length > 0 ? (
                        merchant.addresses.map((a, idx) => {
                          const display = [
                            (a.street1 || a.streetAddress || '').trim(),
                            (a.street2 || a.streetAddress2 || a.unit || '').trim(),
                            (a.city || '').trim(),
                            (a.state || '').trim(),
                            (a.zipCode || '').trim(),
                            (a.country || '').trim(),
                          ].filter(Boolean).join(', ');
                          return (
                            <div key={idx} className="border rounded p-2 mb-1" style={{ fontSize: '0.875rem' }}>
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="badge badge-light mr-2">{a.addressType || 'PHYSICAL'}</span>
                                <span className="text-muted small">{a.city || 'N/A'}, {a.state || 'N/A'}</span>
                              </div>
                              <div className="small mt-1">{display || 'N/A'}</div>
                              {a.phoneNumber && (
                                <div className="small text-muted mt-1">Phone: {a.phoneNumber}</div>
                              )}
                            </div>
                          );  
                        })
                      ) : (
                        <div className="text-muted small">No addresses</div>
                      )}
                    </Col>
                  </Row>

                  {/* Owners summary */}
                  <Row>
                    <Col md="12">
                      <h5 className="text-primary mt-2 mb-2">Owners</h5>
                      {Array.isArray(merchant.owners) && merchant.owners.length > 0 ? (
                        merchant.owners.map((o, idx) => {
                          const ow = o.owner || o;
                          const name = [ow.firstName, ow.middleName, ow.lastName].filter(Boolean).join(' ');
                          return (
                            <div key={idx} className="border rounded p-2 mb-1" style={{ fontSize: '0.875rem' }}>
                              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center">
                                <div>
                                  <strong>{name || 'Unnamed Owner'}</strong>
                                  <div className="text-muted small">Citizenship: {ow.citizenship || 'N/A'}</div>
                                </div>
                                <div className="mt-2 mt-sm-0">
                                  <span className="badge badge-light">{(ow.ownershipPercentage ?? 'N/A')}%</span>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-muted small">No owners</div>
                      )}
                    </Col>
                  </Row>

                  {/* Banks summary */}
                  <Row>
                    <Col md="12">
                      <h5 className="text-primary mt-2 mb-2">Bank Accounts</h5>
                      {Array.isArray(merchant.banks) && merchant.banks.length > 0 ? (
                        merchant.banks.map((b, idx) => {
                          const addressLine = [
                            (b.bankAddress || '').trim(),
                            (b.bankCity || '').trim(),
                            (b.bankState || '').trim(),
                            (b.bankCountry || '').trim(),
                          ].filter(Boolean).join(', ');
                          return (
                            <div key={idx} className="border rounded p-2 mb-1" style={{ fontSize: '0.875rem' }}>
                              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center">
                                <div>
                                  <strong>{b.bankName || 'Bank'}</strong>
                                  <div className="text-muted small">Account: {b.accountName || 'N/A'}</div>
                                  <div className="text-muted small">Number: {b.number || 'N/A'} | Routing: {b.routingNumber || 'N/A'}</div>
                                  {addressLine && (
                                    <div className="text-muted small">{addressLine}</div>
                                  )}
                                </div>
                                <div className="mt-2 mt-sm-0">
                                  <span className="badge badge-light">{(b.type || '').toString().toUpperCase() || 'N/A'}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-muted small">No bank accounts</div>
                      )}
                    </Col>
                  </Row>

                  <Row>
                    <Col md="6">
                      <FormGroup className="mb-2">
                        <Label htmlFor="requestedAmount" className="mb-1">Requested Amount *</Label>
                        <Input
                          id="requestedAmount"
                          type="text"
                          inputMode="numeric"
                          placeholder="0"
                          value={requestedAmount}
                          onChange={handleAmountChange}
                          maxLength={10}
                          className={getFieldError('requestedAmount', requestedAmount) ? 'is-invalid' : ''}
                          bsSize="sm"
                          required
                        />
                        {getFieldError('requestedAmount', requestedAmount) && (
                          <div className="invalid-feedback d-block">{getFieldError('requestedAmount', requestedAmount)}</div>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup className="mb-2">
                        <Label htmlFor="monitoringCenter" className="mb-1">Monitoring Center *</Label>
                        <Input
                          id="monitoringCenter"
                          type="select"
                          value={monitoringCenter}
                          onChange={(e) => {
                            const selectedValue = e.target.value;
                            setMonitoringCenter(selectedValue);
                            // Find the corresponding ID for the selected name
                            const selectedOption = monitoringCenterOptions.find(option => option.value === selectedValue);
                            setMonitoringCenterId(selectedOption ? selectedOption.id : null);
                          }}
                          bsSize="sm"
                          required
                        >
                          <option value="">Select Monitoring Center</option>
                          {monitoringCenterOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup className="mb-2">
                        <Label htmlFor="totalSubscribers" className="mb-1">Total Subscribers *</Label>
                        <Input
                          id="totalSubscribers"
                          type="text"
                          inputMode="numeric"
                          placeholder="0"
                          value={totalSubscribers}
                          onChange={handleSubscribersChange}
                          maxLength={10}
                          className={getFieldError('totalSubscribers', totalSubscribers) ? 'is-invalid' : ''}
                          bsSize="sm"
                          required
                        />
                        {getFieldError('totalSubscribers', totalSubscribers) && (
                          <div className="invalid-feedback d-block">{getFieldError('totalSubscribers', totalSubscribers)}</div>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup className="mb-2">
                        <Label htmlFor="totalMonthlyRevenue" className="mb-1">Total Monthly Revenue *</Label>
                        <Input
                          id="totalMonthlyRevenue"
                          type="text"
                          inputMode="decimal"
                          placeholder="0.00"
                          value={totalMonthlyRevenue}
                          onChange={handleRevenueChange}
                          maxLength={18}
                          className={getFieldError('totalMonthlyRevenue', totalMonthlyRevenue) ? 'is-invalid' : ''}
                          bsSize="sm"
                          required
                        />
                        {getFieldError('totalMonthlyRevenue', totalMonthlyRevenue) && (
                          <div className="invalid-feedback d-block">{getFieldError('totalMonthlyRevenue', totalMonthlyRevenue)}</div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>
                </Form>
              ) : (
                <div className="text-muted">No merchant data available.</div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter className="pt-2">
          <Button color="secondary" onClick={handleToggle} disabled={submitting}>Cancel</Button>
          <Button color="primary" onClick={handleCreate} disabled={!isValid || submitting || loading}>
            {submitting ? (<><Spinner size="sm" className="mr-2" /> Creating...</>) : 'Create Application'}
          </Button>
        </ModalFooter>
      </Modal>

      <ConfirmationModal
        isOpen={modalOpen}
        toggle={closeModal}
        title={modalConfig.title}
        message={modalConfig.message}
        onConfirm={() => { if (typeof modalConfig.onConfirm === 'function') modalConfig.onConfirm(); closeModal(); }}
        onCancel={() => { if (typeof modalConfig.onCancel === 'function') modalConfig.onCancel(); closeModal(); }}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
        confirmColor={modalConfig.confirmColor}
        cancelColor={modalConfig.cancelColor}
        icon={modalConfig.icon}
      />
    </>
  );
}

export default CreateApplicationModal;
